import * as React from 'react';
import { DatePicker, PickersDay } from '@mui/x-date-pickers';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import { isToday } from 'date-fns';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { formatDate } from '../../../../shared/LanguageService';
import addDays from 'date-fns/addDays';
import subDays from 'date-fns/subDays';
import { getPublicHolidays } from '../queries/getPublicHolidays';
import { handleError } from '../../../../shared/ErrorCatching';
import Skeleton from '@mui/material/Skeleton';

type ExternalProps = {
    user: any;
    selectedDate: Date;
    onDateChange: (newValue) => void;
    selectedDay: string;
    onChangeBookingDate: (day: Date) => void;
    selectedBookingDate: Date;
};

type DatePickerProps = {
    getHolidays: (year: number) => void;
    publicHolidays: string[] | [];
} & ExternalProps;

type ApiResponse = {
    isLoading: boolean;
    isError: boolean;
    error: any;
    data: any;
};

enum Weekday {
    Monday = 1,
    Tuesday = 2,
    Wednesday = 3,
    Thursday = 4,
    Friday = 5,
    Saturday = 6,
    Sunday = 0
}

const StyledDatePicker = (props: DatePickerProps) => {
    let {
        selectedDate,
        onDateChange,
        selectedDay,
        getHolidays,
        publicHolidays,
        onChangeBookingDate,
        selectedBookingDate
    } = props;

    let formattedSelectedDate = formatDate(new Date(selectedDate), 'dd MMMM yyyy');

    const handleNextDayClick = () => {
        let nextDay = addDays(new Date(selectedBookingDate), 1);
        onChangeBookingDate(nextDay);
    };

    const handlePreviousDayClick = () => {
        let prevDay = subDays(new Date(selectedBookingDate), 1);
        onChangeBookingDate(prevDay);
    };

    return (
        <Box sx={{ width: '100%', paddingRight: '6px' }} aria-label="date-picker">
            <DatePicker
                disablePast={true}
                value={selectedDate}
                onChange={(newValue) => onDateChange(newValue)}
                onYearChange={(newValue) => {
                    getHolidays(newValue.getFullYear());
                }}
                onMonthChange={(newValue) => {
                    getHolidays(newValue.getFullYear());
                }}
                renderDay={(day, selectedDays, pickersDayProps) => {
                    const date = new Date(day);
                    let weekDay = date.getDay();
                    if (weekDay === Weekday.Saturday || weekDay === Weekday.Sunday) {
                        return (
                            <PickersDay
                                {...pickersDayProps}
                                sx={{
                                    backgroundColor: '#F5F5F5',
                                    '&:hover': { border: '1px solid rgba(0, 0, 0, 0.54)' }
                                }}
                            />
                        );
                    }

                    if (publicHolidays.length) {
                        for (const holiday of publicHolidays) {
                            if (day.getTime() === new Date(holiday).getTime()) {
                                return (
                                    <PickersDay
                                        {...pickersDayProps}
                                        sx={{
                                            color: '#E31B0C',
                                            fontWeight: 'bold',
                                            '&:hover': { border: '1px solid rgba(0, 0, 0, 0.54)' }
                                        }}
                                    />
                                );
                            }
                        }
                    }
                    return (
                        <PickersDay
                            {...pickersDayProps}
                            sx={{ '&:hover': { border: '1px solid rgba(0, 0, 0, 0.54)' } }}
                        />
                    );
                }}
                renderInput={({ ...params }) => (
                    <Box
                        ref={params.inputRef}
                        sx={{
                            display: 'flex',
                            width: '400px',
                            height: '65px',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#F5F5F5',
                            borderRadius: '4px',
                            position: 'relative'
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                position: 'absolute',
                                left: '50px'
                            }}
                        >
                            <Typography
                                sx={{
                                    fontWeight: 700,
                                    paddingRight: '7px',
                                    fontSize: '15px',
                                    textTransform: 'capitalize'
                                }}
                            >
                                {selectedDay},
                            </Typography>
                            <Typography sx={{ fontWeight: 400 }}>
                                {formattedSelectedDate}
                            </Typography>
                        </Box>
                        <IconButton
                            disabled={isToday(selectedDate)}
                            onClick={handlePreviousDayClick}
                            sx={{ position: 'absolute', right: '115px' }}
                        >
                            <ChevronLeft />
                        </IconButton>
                        <IconButton
                            onClick={handleNextDayClick}
                            sx={{ position: 'absolute', right: '70px' }}
                        >
                            <ChevronRight />
                        </IconButton>
                        <Box
                            sx={{
                                position: 'absolute',
                                right: '30px',
                                '& .MuiSvgIcon-root': {
                                    height: '20px',
                                    width: 'auto',
                                    color: 'rgba(0, 0, 0, 0.54)'
                                }
                            }}
                        >
                            {params.InputProps && params.InputProps.endAdornment}
                        </Box>
                    </Box>
                )}
            />
        </Box>
    );
};

const StyledDatePickerDataLayer = (props: ExternalProps) => {
    const [year, setYear] = React.useState<number>(new Date().getFullYear());

    const getHolidays = (year: number) => {
        setYear(year);
    };

    const { data, isLoading, isError, error }: ApiResponse = getPublicHolidays(
        year,
        props.user.details.country
    );

    if (isError) {
        handleError(error);
    }

    if (isLoading) {
        return (
            <Skeleton
                variant="rectangular"
                sx={{
                    minWidth: '400px',
                    height: '65px',
                    marginRight: '6px',
                    borderRadius: '4px',
                    backgroundColor: '#F5F5F5'
                }} />
        );
    }

    return (
        <StyledDatePicker
            {...props}
            getHolidays={(year) => getHolidays(year)}
            publicHolidays={data || []}
        />
    );
};

export default StyledDatePickerDataLayer;
